import { Trash2 } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useObsWizard } from "@/features/obs/hooks/obs-wizard-context";

/**
 * Asked when the operator tries to leave a report that ALREADY EXISTS.
 *
 * The report genuinely has to exist by the units step — the workbook uploads
 * against its slug and the preview renders from it — so what is wrong is not
 * that leaving is possible, but that leaving AFTER creation was treated as
 * identical to leaving before it. Both answers are offered because both are
 * legitimate: keep it as a draft and finish from its card later, or delete it.
 */
export function ObsDiscardDraftDialog() {
  const {
    draft,
    discarding,
    closeDiscardPrompt,
    keepDraft,
    discardDraft,
    removing,
  } = useObsWizard();

  return (
    <Dialog
      open={discarding}
      onOpenChange={(open) => {
        if (!open && !removing) closeDiscardPrompt();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>هذا التقرير أُنشئ بالفعل</DialogTitle>
          <DialogDescription>
            الخروج الآن لا يتراجع عمّا حُفظ على الخادم.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
            <p className="text-sm text-muted-foreground">
              أُنشئ «{draft.titleAr.trim() || draft.slug}» ورُفعت بياناته عند
              خطوة «الوحدات»، لأن الملف يُرفع على التقرير نفسه ومنه تُعرض
              المعاينة.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              إن أُبقي عليه فسيبقى في لوحة التحكم <b>غير منشور</b> — لا يظهر على
              الموقع — وتظهر على بطاقته علامة «مسودة» وزر{" "}
              <b>«نشر على الموقع»</b> لإكماله متى شئت.
            </p>
          </div>

          {removing && (
            <p className="text-sm text-muted-foreground">جارٍ حذف المسودة…</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={closeDiscardPrompt}
            disabled={removing}
          >
            متابعة الإعداد
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={keepDraft} disabled={removing}>
              الاحتفاظ به كمسودة
            </Button>
            <Button
              variant="destructive"
              onClick={discardDraft}
              disabled={removing}
            >
              {removing ? <Loader /> : <Trash2 className="size-4" />}
              حذف المسودة
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
